import '@material/web/icon/icon.js'
import '@operato/input/ox-input-select-buttons.js'
import '@operato/data-grist'
import '@operato/context/ox-context-page-toolbar.js'

import gql from 'graphql-tag'
import { css, html, nothing } from 'lit'
import { customElement, property, query, state } from 'lit/decorators.js'
import { connect } from 'pwa-helpers/connect-mixin'

import { DataGrist, FetchHandler, FetchOption, GristRecord } from '@operato/data-grist'
import { client } from '@operato/graphql'
import { i18next, localize } from '@operato/i18n'
import { PageView, store } from '@operato/shell'
import { CommonHeaderStyles, ScrollbarStyles } from '@operato/styles'
import { isMobileDevice } from '@operato/utils'
import { DataItem } from '@operato/dataset'

enum DataSetSummaryGroupType {
  Today = 'today',
  ThisMonth = 'this month',
  ThirtyDays = '30 days',
  ThisYear = 'this year',
  TwelveMonths = '12 months',
  DateRange = 'date range',
  Month = 'month'
}

@customElement('data-summary-period-page')
export class DataSummaryGroupPage extends connect(store)(localize(i18next)(PageView)) {
  static styles = [
    ScrollbarStyles,
    CommonHeaderStyles,
    css`
      :host {
        display: flex;
        flex-direction: column;

        overflow: hidden;
      }

      ox-grist {
        overflow-y: auto;
        flex: 1;
      }

      .header {
        grid-template-areas: 'filters actions';
      }
    `
  ]

  @state() dataSetId?: string
  @state() period?: string = DataSetSummaryGroupType.ThisMonth
  @state() withKeys: boolean = false

  @state() dataSet: any
  @state() dataKeySet: any
  @state() dataItems?: DataItem[]

  @state() gristConfig: any
  @state() mode: 'CARD' | 'GRID' | 'LIST' = isMobileDevice() ? 'CARD' : 'GRID'

  @query('ox-grist') private grist!: DataGrist

  get context() {
    return {
      title: i18next.t('title.data-summary period'),
      help: 'dataset/data-summary-period',
      actions: [
        {
          type: 'icon',
          icon: 'search',
          action: () => {
            this.grist.fetch()
          }
        }
      ],
      exportable: {
        name: i18next.t('title.data-summary period'),
        data: this._exportableData.bind(this)
      },
      toolbar: false
    }
  }

  render() {
    const mode = this.mode || (isMobileDevice() ? 'LIST' : 'GRID')

    return html`
      <ox-grist .mode=${mode} .config=${this.gristConfig} .fetchHandler=${this.fetchHandler.bind(this) as FetchHandler}>
        <div slot="headroom" class="header">
          <div
            class="filters"
            @change=${(e: CustomEvent) => {
              const element = e.target as Element
              if (element?.hasAttribute('period')) {
                this.period = (element as any)?.value
              } else if (element?.hasAttribute('with-keys')) {
                this.withKeys = (element as any)?.checked
              }

              this.refreshGristConfig()
            }}
          >
            <ox-input-select-buttons
              class="filter"
              .value=${this.period}
              .options=${[
                { display: i18next.t('label.period-this-month'), value: 'this month' },
                { display: i18next.t('label.period-30-days'), value: '30 days' },
                { display: i18next.t('label.period-this-year'), value: 'this year' },
                { display: i18next.t('label.period-12-months'), value: '12 months' },
                { display: i18next.t('label.period-today'), value: 'today' },
                { display: i18next.t('label.period-month'), value: 'month' },
                { display: i18next.t('label.period-date-range'), value: 'date range' }
              ]}
              period
            ></ox-input-select-buttons>

            ${this.period == 'date range' || this.period == 'month'
              ? html`<ox-filters-form without-search></ox-filters-form>`
              : nothing}

            <ox-checkbox class="filter" with-keys>${i18next.t('label.group-by data-keys')}</ox-checkbox>
          </div>

          <ox-context-page-toolbar class="actions" .context=${this.context}></ox-context-page-toolbar>
        </div>
      </ox-grist>
    `
  }

  pageUpdated(changes, lifecycle) {
    if (this.active) {
      this.dataSetId = lifecycle.resourceId
      return
    }
  }

  async updated(changes) {
    if (changes.has('dataSetId')) {
      const response = await client.query({
        query: gql`
          query ($id: String!) {
            dataSet(id: $id) {
              id
              name
              summaryPeriod
              dataItems {
                name
                description
                active
                hidden
                tag
                group
                type
                unit
                options
                quota
                spec
                stat
              }
              dataKeySet {
                id
                name
                description
                dataKeyItems {
                  name
                  description
                  dataKey
                  tKey
                }
              }
            }
          }
        `,
        variables: {
          id: this.dataSetId
        }
      })

      this.dataSet = response.data?.dataSet
      this.dataKeySet = this.dataSet?.dataKeySet || {}
      this.dataItems = this.dataSet?.dataItems || []

      this.refreshGristConfig()
      this.grist.fetch()
    }
  }

  getKeyColumns() {
    if (!this.withKeys) {
      return []
    }

    return (
      this.dataKeySet?.dataKeyItems.map((item, index) => {
        return {
          type: 'string',
          name: `key0${index + 1}`,
          header: i18next.t(item.tKey),
          label: true,
          record: {
            editable: false
          },
          sortable: true,
          width: 120,
          imex: true
        }
      }) || []
    )
  }

  getDataColumns() {
    return (
      this.dataItems
        ?.filter(dataItem => dataItem.active && dataItem.stat)
        .map((item, index) => {
          return {
            type: 'number',
            name: `data0${index + 1}`,
            header: i18next.t(item.name),
            label: true,
            record: {
              editable: false
            },
            width: 120,
            imex: true
          }
        }) || []
    )
  }

  refreshGristConfig() {
    const keyColumns = this.getKeyColumns()
    const dataColumns = this.getDataColumns()

    const listFields = [] as string[]
    const monthFieldRequired =
      this.period == DataSetSummaryGroupType.ThisYear ||
      this.period == DataSetSummaryGroupType.TwelveMonths ||
      this.period == DataSetSummaryGroupType.Month
    const dateFieldRequired =
      this.period != DataSetSummaryGroupType.ThisYear && this.period != DataSetSummaryGroupType.TwelveMonths
    const periodRequired = this.period == DataSetSummaryGroupType.Today

    monthFieldRequired && listFields.push('month')
    dateFieldRequired && listFields.push('date')
    periodRequired && listFields.push('period')

    keyColumns.map(col => listFields.push(col.name))
    dataColumns.map(col => listFields.push(col.name))

    listFields.push('count', 'countOoc', 'countOos')

    this.gristConfig = {
      list: { fields: listFields },
      columns: [
        { type: 'gutter', gutterName: 'sequence', fixed: true },
        monthFieldRequired && {
          type: 'string',
          name: 'month',
          fixed: true,
          header: i18next.t('field.month'),
          label: true,
          width: 80,
          sortable: true,
          filter: this.period == DataSetSummaryGroupType.Month && {
            type: 'month',
            operator: 'eq',
            value:
              this.grist.filters?.find(({ name }) => name == 'month')?.value ||
              new Date().toLocaleDateString('sv-SE').slice(0, 7)
          },
          hidden: this.period == DataSetSummaryGroupType.Month,
          imex: true
        },
        dateFieldRequired && {
          type: 'string',
          name: 'date',
          fixed: true,
          header: i18next.t('field.date'),
          label: true,
          sortable: true,
          width: 100,
          filter: this.period == DataSetSummaryGroupType.DateRange && {
            type: 'date',
            operator: 'between',
            value: this.grist.filters?.find(({ name }) => name == 'date')?.value || [
              new Date(new Date().getFullYear(), new Date().getMonth(), 1).toLocaleDateString('sv-SE'),
              new Date().toLocaleDateString('sv-SE')
            ]
          },
          imex: true
        },
        periodRequired && {
          type: 'string',
          name: 'period',
          fixed: true,
          header: i18next.t('field.time-period'),
          label: true,
          sortable: true,
          width: 80,
          imex: true
        },
        ...keyColumns,
        ...dataColumns,
        {
          type: 'number',
          name: 'count',
          header: i18next.t('field.count'),
          label: true,
          record: {
            editable: false
          },
          width: 30
        },
        {
          type: 'number',
          name: 'countOoc',
          label: true,
          header: i18next.t('field.count-ooc'),
          record: {
            editable: false
          },
          width: 30
        },
        {
          type: 'number',
          name: 'countOos',
          header: i18next.t('field.count-oos'),
          label: true,
          record: {
            editable: false
          },
          width: 30
        }
      ].filter(Boolean),
      pagination: {
        infinite: true
      },
      rows: {
        appendable: false,
        selectable: {
          multiple: true
        }
      }
    }
  }

  async fetchHandler({ page, limit, sortings = [], filters = [] }: FetchOption) {
    if (!this.dataSetId) {
      return { records: [], total: 0 }
    }

    if (this.period == 'date range' || this.period == 'month') {
      const { fromDate, toDate, month } = filters.reduce(
        (acc, filter) => {
          if (filter.name === 'fromDate') acc.fromDate = filter.value
          if (filter.name === 'toDate') acc.toDate = filter.value
          if (filter.name === 'month') acc.month = filter.value
          return acc
        },
        { fromDate: null, toDate: null, month: null }
      )
    }

    const response = await client.query({
      query: gql`
        query (
          $dataSetName: String!
          $period: String!
          $dataKeys: [String!]
          $filters: [Filter!]
          $pagination: Pagination
          $sortings: [Sorting!]
        ) {
          dataSummaryByPeriod(
            dataSetName: $dataSetName
            period: $period
            dataKeys: $dataKeys
            filters: $filters
            pagination: $pagination
            sortings: $sortings
          ) {
            month
            date
            period
            key01
            key02
            key03
            key04
            key05
            data01
            data02
            data03
            data04
            data05
            count
            countOoc
            countOos
          }
        }
      `,
      variables: {
        dataSetName: this.dataSet.name,
        period: this.period,
        dataKeys: this.withKeys ? null : [],
        filters,
        pagination: { page, limit },
        sortings
      }
    })

    return {
      records: response.data?.dataSummaryByPeriod || []
    }
  }

  _exportableData() {
    let records = [] as GristRecord[]
    if (this.grist.selected && this.grist.selected.length > 0) {
      records = this.grist.selected
    } else {
      records = this.grist.data.records
    }

    var headerSetting = this.grist.compiledConfig.columns
      .filter(column => column.type !== 'gutter' && column.record !== undefined && column.imex !== undefined)
      .map(column => {
        return column.imex === true
          ? {
              header: column.header.renderer(column),
              key: column.name,
              width: column.width,
              type: column.type
            }
          : column.imex
      })

    var data = records.map(item => {
      return {
        id: item.id,
        ...this.gristConfig.columns
          .filter(column => column.type !== 'gutter' && column.record !== undefined && column.imex !== undefined)
          .reduce((record, column) => {
            const key = column.imex === true ? column.name : column.imex.key
            record[key] = key
              .split('.')
              .reduce((obj, key) => (obj && obj[key] !== 'undefined' ? obj[key] : undefined), item)
            return record
          }, {})
      }
    })

    return { header: headerSetting, data: data }
  }
}
